import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { 
  StripeLogo, 
  GoogleLogo, 
  CheckCircle, 
  Warning,
  ArrowRight,
  ArrowLeft,
  Spinner,
  PlugsConnected
} from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'

type IntegrationsStepProps = {
  onNext: () => void
  onBack: () => void
  onSkip: () => void
  status: {
    stripe: boolean
    calendar: boolean
  } | null
  onRefresh: () => Promise<void>
}

type IntegrationCardProps = {
  id: string
  name: string
  description: string
  icon: typeof StripeLogo
  iconColor: string
  bgColor: string
  connected: boolean
  required: boolean
  onConnect: () => void
  connecting: boolean
}

function IntegrationCard({
  id,
  name,
  description,
  icon: Icon,
  iconColor,
  bgColor,
  connected,
  required,
  onConnect,
  connecting,
}: IntegrationCardProps) {
  const { t } = useTranslation('onboarding')

  return (
    <Card
      className={cn(
        'relative overflow-hidden transition-all duration-300',
        connected
          ? 'border-green-500/50 bg-green-50/50 dark:bg-green-950/20'
          : 'hover:border-[var(--pd-accent)]/50 hover:shadow-md'
      )}
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div
            className={cn(
              'flex items-center justify-center w-12 h-12 rounded-xl',
              bgColor
            )}
          >
            <Icon weight="bold" className={cn('w-6 h-6', iconColor)} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold text-foreground">
                {name}
              </h4>
              {required && (
                <Badge variant="secondary" className="text-xs">
                  {t('integrations.required')}
                </Badge>
              )}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
              {description}
            </p>
          </div>

          {/* Status / Action */}
          <div className="flex-shrink-0">
            {connected ? (
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                <CheckCircle weight="fill" className="w-5 h-5" />
                <span className="text-sm font-medium">
                  {t('integrations.connected')}
                </span>
              </div>
            ) : (
              <Button
                onClick={onConnect}
                disabled={connecting}
                size="sm"
                variant="outline"
                className="border-[var(--pd-accent)] text-[var(--pd-accent)] hover:bg-[var(--pd-accent)] hover:text-white"
              >
                {connecting ? (
                  <Spinner className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                {t('integrations.connect')}
              </Button>
            )}
          </div>
        </div>

        {/* Connected indicator line */}
        {connected && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-green-400 to-green-500" />
        )}
      </CardContent>
    </Card>
  )
}

export function IntegrationsStep({
  onNext,
  onBack,
  onSkip,
  status,
  onRefresh,
}: IntegrationsStepProps) {
  const { t } = useTranslation('onboarding')
  const [searchParams] = useSearchParams()
  const [connectingStripe, setConnectingStripe] = useState(false)
  const [connectingGoogle, setConnectingGoogle] = useState(false)

  const stripeConnected = status?.stripe ?? false
  const googleConnected = status?.calendar ?? false
  const anyConnected = stripeConnected || googleConnected
  const allConnected = stripeConnected && googleConnected

  const handleConnectStripe = useCallback(async () => {
    try {
      setConnectingStripe(true)
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000'
      
      const response = await fetch(`${apiUrl}/api/integrations/stripe/auth-url`, {
        credentials: 'include',
      })

      if (response.ok) {
        const data = await response.json()
        if (data.authUrl) {
          window.location.href = data.authUrl
        }
      } else {
        toast.error(t('integrations.error.stripe'))
      }
    } catch (error) {
      console.error('Failed to connect Stripe:', error)
      toast.error(t('integrations.error.stripe'))
    } finally {
      setConnectingStripe(false)
    }
  }, [t])

  const handleConnectGoogle = useCallback(async () => {
    try {
      setConnectingGoogle(true)
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000'
      
      const response = await fetch(`${apiUrl}/api/integrations/google/auth-url`, {
        credentials: 'include',
      })

      if (response.ok) {
        const data = await response.json()
        if (data.authUrl) {
          window.location.href = data.authUrl
        }
      } else {
        toast.error(t('integrations.error.google'))
      }
    } catch (error) {
      console.error('Failed to connect Google:', error)
      toast.error(t('integrations.error.google'))
    } finally {
      setConnectingGoogle(false)
    }
  }, [t])

  // Refresh status on mount and when returning from OAuth
  useEffect(() => {
    onRefresh()
  }, [onRefresh])

  // Refresh again when returning from Google callback
  useEffect(() => {
    const refreshParam = searchParams.get('refresh')
    if (refreshParam === 'google') {
      onRefresh()
      toast.success(t('integrations.google.connectedToast'))
    }
  }, [searchParams, onRefresh, t])

  return (
    <div className="flex flex-col px-4 py-6 animate-in fade-in duration-500">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[var(--pd-primary-bg)] mb-4">
          <PlugsConnected weight="bold" className="w-7 h-7 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2">
          {t('integrations.title')}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
          {t('integrations.subtitle')}
        </p>
      </div>

      {/* Integration Cards */}
      <div className="space-y-4 max-w-lg mx-auto w-full">
        <IntegrationCard
          id="stripe"
          name="Stripe Connect"
          description={t('integrations.stripe.description')}
          icon={StripeLogo}
          iconColor="text-purple-600"
          bgColor="bg-purple-50 dark:bg-purple-950/30"
          connected={stripeConnected}
          required={true}
          onConnect={handleConnectStripe}
          connecting={connectingStripe}
        />

        <IntegrationCard
          id="google"
          name="Google Calendar"
          description={t('integrations.google.description')}
          icon={GoogleLogo}
          iconColor="text-blue-600"
          bgColor="bg-blue-50 dark:bg-blue-950/30"
          connected={googleConnected}
          required={false}
          onConnect={handleConnectGoogle}
          connecting={connectingGoogle}
        />
      </div>

      {/* Warning for non-connected */}
      {!allConnected && (
        <div
          className={cn(
            'flex items-start gap-3 p-4 mt-6 rounded-lg max-w-lg mx-auto w-full',
            'bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/50'
          )}
        >
          <Warning weight="fill" className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
              {!stripeConnected 
                ? t('integrations.warning.stripeRequired')
                : t('integrations.warning.googleRecommended')
              }
            </p>
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
              {t('integrations.warning.canSkip')}
            </p>
          </div>
        </div>
      )}

      {/* Success message */}
      {allConnected && (
        <div
          className={cn(
            'flex items-center gap-3 p-4 mt-6 rounded-lg max-w-lg mx-auto w-full',
            'bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800/50'
          )}
        >
          <CheckCircle weight="fill" className="w-5 h-5 text-green-500" />
          <p className="text-sm font-medium text-green-800 dark:text-green-200">
            {t('integrations.allConnected')}
          </p>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between mt-10 max-w-lg mx-auto w-full">
        <Button
          onClick={onBack}
          variant="ghost"
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400"
        >
          <ArrowLeft weight="bold" className="mr-2 w-4 h-4" />
          {t('back')}
        </Button>

        <div className="flex items-center gap-3">
          {!allConnected && (
            <Button
              onClick={onSkip}
              variant="ghost"
              className="text-gray-400 hover:text-gray-600 dark:text-gray-500"
            >
              {t('skipStep')}
            </Button>
          )}
          
          <Button
            onClick={onNext}
            className={cn(
              'bg-[var(--pd-accent)] hover:opacity-90 text-white',
              !anyConnected && 'opacity-90'
            )}
          >
            {t('continue')}
            <ArrowRight weight="bold" className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
